import { Location } from '@angular/common';
import { Component, inject, OnInit, OnDestroy, signal, HostListener } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ProductoList } from '@app/entidades/operacion.model';
import { StorageService } from '@app/servicios/storage/storage.service';
import { SearchService } from '@app/servicios/search/search.service';
import { environment } from '@env';
import { Subject, Subscription, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FiltroProductos, Producto } from 'src/app/entidades/producto.model';
import { ProductoService } from 'src/app/servicios/producto/producto.service';

@Component({
  selector: 'app-tienda',
  templateUrl: './tienda.component.html',
  styleUrls: ['./tienda.component.scss'],
})
export class TiendaComponent implements OnInit, OnDestroy {
  public suscription: Subscription = new Subscription();
  private searchSubject: Subject<string> = new Subject<string>();
  fotoUrl = environment.servImgURL + '/';

  private router = inject(Router);

  breadcrumb = signal<any>({
    cat: '',
    tipo: '',
    subtipo: '',
  });

  local = true;

  cat = '';
  tipo = '';
  sub = '';

  public productos: Producto[] = [];
  public tipos = [];
  public subtipos = [];
  public formFiltro: FormGroup;
  public lista: ProductoList[] = [];
  public alertOk = false;
  public buscado = '';
  public ordenSeleccionado = 'ultimoIngreso';

  isFirst = false;
  isLast = false;

  page: any = 0;
  size = 30;
  listPages!: Array<number>;
  totalPages = 0;

  isMobile = false;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isMobile = typeof window !== 'undefined' ? window.innerWidth < 900 : false;
  }

  constructor(
    private productoServ: ProductoService,
    private builder: FormBuilder,
    private _route: ActivatedRoute,
    private storageService: StorageService,
    private location: Location,
    private searchService: SearchService
  ) {
    this.formFiltro = builder.group({
      categoria: '',
      tipo: '',
      subtipo: '',
    });
  }

  goBack() {
    this.location.back();
  }

  ngOnInit() {
    this.onResize();
    this.suscription.add(
      this.searchService.searchQuery$.subscribe((query) => {
        if (this.buscado !== query) {
          this.buscado = query;
          this.consultarProductos(0);
        }
      })
    );

    this._route.paramMap.subscribe((params: ParamMap) => {
      this.cat = params.get('cat') || 'all';
      this.tipo = params.get('tipo') || 'all';
      this.sub = params.get('sub') || 'all';

      if (this.cat !== 'all') {
        this.formFiltro.get('categoria')?.setValue(this.cat);
      } else {
        this.formFiltro.get('categoria')?.setValue('');
      }
      if (this.tipo !== 'all') {
        this.formFiltro.get('tipo')?.setValue(this.tipo);
      } else {
        this.formFiltro.get('tipo')?.setValue('');
      }
      if (this.sub !== 'all') {
        this.formFiltro.get('subtipo')?.setValue(this.sub);
      } else {
        this.formFiltro.get('subtipo')?.setValue('');
      }

      this.page = Number(params.get('page')) || 0;

      this.consultarProductos(this.page);
    });

    this.consultarTipos();
  }

  limpiarBuscado() {
    this.buscado = '';
    this.searchService.clearSearch();
    this.consultarProductos(0);
  }

  hasActiveFilters(): boolean {
    return (
      (this.cat && this.cat !== 'all') ||
      (this.tipo && this.tipo !== 'all') ||
      (this.sub && this.sub !== 'all') ||
      !!this.buscado ||
      this.local
    );
  }

  removerFiltro(tipoFiltro: 'cat' | 'tipo' | 'sub' | 'local') {
    if (tipoFiltro === 'cat') {
      this.cat = 'all';
      this.tipo = 'all';
      this.sub = 'all';
      this.formFiltro.patchValue({ categoria: '', tipo: '', subtipo: '' });
      this.router.navigateByUrl(`tienda/all/all/all/0`);
    } else if (tipoFiltro === 'tipo') {
      this.tipo = 'all';
      this.sub = 'all';
      this.formFiltro.patchValue({ tipo: '', subtipo: '' });
      const catVal = this.cat || 'all';
      this.router.navigateByUrl(`tienda/${catVal}/all/all/0`);
    } else if (tipoFiltro === 'sub') {
      this.sub = 'all';
      this.formFiltro.patchValue({ subtipo: '' });
      const catVal = this.cat || 'all';
      const tipoVal = this.tipo || 'all';
      this.router.navigateByUrl(`tienda/${catVal}/${tipoVal}/all/0`);
    } else if (tipoFiltro === 'local') {
      this.local = false;
      this.consultarProductos(0);
    }
  }

  cambiarOrden(orden: string) {
    this.ordenSeleccionado = orden;
    this.consultarProductos(0);
  }

  consultarProductos(page: any) {
    this.page = Number(page) || 0;
    const localValue = this.local ? 0 : null;

    const filter = {
      activo: 'ACTIVO',
      id: '',
      nombre: this.buscado,
      categoria: this.formFiltro.get('categoria')?.value,
      tipo: this.formFiltro.get('tipo')?.value,
      subtipo: this.formFiltro.get('subtipo')?.value,
      codPrecio: '',
      esNuevo: '',
      visible: true,
      local: localValue,
      ordenarPor: this.ordenSeleccionado || 'ultimoIngreso',
    };

    this.breadcrumb.set({
      cat: filter.categoria,
      tipo: filter.tipo,
      subtipo: filter.subtipo,
    });

    this.suscription.add(
      this.productoServ
        .filtrarProductos(this.page, this.size, filter)
        .subscribe((resp) => {
          this.productos = resp.data || [];

          const totalItems = Number(resp.mensaje) || 0;
          this.totalPages = Math.max(1, Math.ceil(totalItems / this.size));
          this.listPages = new Array(this.totalPages);
          this.isFirst = this.page <= 0;
          this.isLast = this.page >= this.totalPages - 1;
        })
    );

    this.consultarTipos();
  }

  consultarTipos() {
    this.suscription.add(
      this.productoServ
        .consultarTipos(this.formFiltro.get('categoria')?.value)
        .subscribe((resp) => {
          this.tipos = resp.data;
        })
    );

    this.suscription.add(
      this.productoServ
        .consultarSubtipos(this.formFiltro.get('tipo')?.value)
        .subscribe((resp) => {
          this.subtipos = resp.data;
        })
    );
  }

  cambiarCategoria() {
    this.storageService.setItem('page', 0);
    if (this.formFiltro.get('categoria').value === '') {
      this.formFiltro.get('categoria').setValue('all');
    }
    this.formFiltro.get('tipo').setValue('all');
    this.formFiltro.get('subtipo').setValue('all');

    this.router.navigateByUrl(
      `tienda/${this.formFiltro.get('categoria').value}/${
        this.formFiltro.get('tipo').value
      }/${this.formFiltro.get('subtipo').value}/0`
    );
  }

  cambiarTipo() {
    this.storageService.setItem('page', 0);

    if (this.formFiltro.get('categoria').value === '') {
      this.formFiltro.get('categoria').setValue('all');
    }

    if (this.formFiltro.get('tipo').value === '') {
      this.formFiltro.get('tipo').setValue('all');
    }

    this.formFiltro.get('subtipo').setValue('all');

    this.router.navigateByUrl(
      `tienda/${this.formFiltro.get('categoria').value}/${
        this.formFiltro.get('tipo').value
      }/${this.formFiltro.get('subtipo').value}/0`
    );
  }

  cambiarSubtipo() {
    this.storageService.setItem('page', 0);

    if (this.formFiltro.get('subtipo').value === '') {
      this.formFiltro.get('subtipo').setValue('all');
    }

    this.router.navigateByUrl(
      `tienda/${this.formFiltro.get('categoria').value}/${
        this.formFiltro.get('tipo').value
      }/${this.formFiltro.get('subtipo').value}/0`
    );
  }

  limpiarFiltro() {
    this.storageService.setItem('page', 0);
    this.formFiltro = this.builder.group({
      //nombre: '',
      categoria: '',
      tipo: '',
      subtipo: '',
    });
    this.router.navigateByUrl(`tienda/all/all/all/0`);
  }

  anterior() {
    if (this.page > 0) {
      this.page--;
      if (this.formFiltro.get('categoria').value === '') {
        this.formFiltro.get('categoria').setValue('all');
      }
      if (this.formFiltro.get('tipo').value === '') {
        this.formFiltro.get('tipo').setValue('all');
      }
      if (this.formFiltro.get('subtipo').value === '') {
        this.formFiltro.get('subtipo').setValue('all');
      }

      var url: string =
        'tienda/' +
        this.formFiltro.get('categoria').value +
        '/' +
        this.formFiltro.get('tipo').value +
        '/' +
        this.formFiltro.get('subtipo').value +
        '/' +
        this.page;

      this.router.navigateByUrl(url);
    }
  }

  siguiente() {
    if (this.page < this.totalPages - 1) {
      this.page++;

      if (this.formFiltro.get('categoria').value === '') {
        this.formFiltro.get('categoria').setValue('all');
      }
      if (this.formFiltro.get('tipo').value === '') {
        this.formFiltro.get('tipo').setValue('all');
      }
      if (this.formFiltro.get('subtipo').value === '') {
        this.formFiltro.get('subtipo').setValue('all');
      }

      var url: string =
        'tienda/' +
        this.formFiltro.get('categoria').value +
        '/' +
        this.formFiltro.get('tipo').value +
        '/' +
        this.formFiltro.get('subtipo').value +
        '/' +
        this.page;

      this.router.navigateByUrl(url);
    }
  }

  agregarProducto(producto: Producto) {
    if (!producto || !producto.stock) {
      return;
    }

    const maxAvailable = Number(producto.stock?.stock ?? 0);
    const localStock = Number(producto.stock?.local ?? 0);
    if (!Number.isFinite(maxAvailable) || maxAvailable <= 0 || !Number.isFinite(localStock) || localStock <= 0) {
      return;
    }

    this.lista = this.storageService.getItem('lp') || [];

    const existing = this.lista.find((p) => p.id === producto.id);
    if (existing) {
      if (existing.cantidad < maxAvailable) {
        existing.cantidad++;
      } else {
        return;
      }
    } else {
      let prod: ProductoList = {
        id: producto.id,
        nombre: producto.nombre,
        foto: this.fotoUrl + producto.foto,
        cantidad: 1,
        stock: maxAvailable,
        tipo: producto.tipo,
        precio: producto.precios,
      };

      this.lista.push(prod);
    }

    this.storageService.setItem('lp', this.lista);
    this.alertSuccess();
  }

  alertSuccess() {
    this.alertOk = true;
    timer(1500).subscribe((N) => (this.alertOk = false));
  }

  buscar() {
    const localValue = this.local ? 0 : null;
    const filter = {
      activo: 'ACTIVO',
      id: '',
      nombre: this.buscado,
      categoria: '',
      tipo: '',
      subtipo: '',
      codPrecio: '',
      esNuevo: '',
      visible: true,
      local: localValue,
      ordenarPor: 'ultimoIngreso',
    };

    const page = 0;

    this.suscription.add(
      this.productoServ
        .filtrarProductos(page, this.size, filter)
        .subscribe((resp) => {
          this.productos = resp.data;

          this.totalPages = Math.ceil(resp.mensaje / 20);
          this.listPages = new Array(this.totalPages);
          this.isFirst = true;
          this.isLast = this.totalPages <= 1;
        })
    );

    this.consultarTipos();
  }

  ngOnDestroy(): void {
    this.suscription.unsubscribe();
  }
}
